/**
 * Employee Offboarding Modal Component
 * Flag assets as pending return when employee is offboarding
 */
import React, { useState, useEffect, useCallback } from 'react';
import { X, UserMinus, Package, AlertTriangle, CheckCircle, Search, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getToken = () => localStorage.getItem('token');

const apiCall = async (endpoint, options = {}) => {
  const token = getToken();
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }
  
  return response.json();
};

// User Search Component
const UserSearch = ({ users, selectedUser, onSelect }) => {
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  
  // Handle different user data structures (full_name vs first_name/last_name, is_active vs status)
  const getUserName = (user) => {
    if (user.full_name) return user.full_name;
    if (user.first_name || user.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return user.email;
  };
  
  const isUserActive = (user) => {
    if (typeof user.is_active === 'boolean') return user.is_active;
    if (user.status) return user.status === 'active';
    return true; // Default to active if no status field
  };
  
  const filteredUsers = users.filter(user => {
    const userName = getUserName(user).toLowerCase();
    const userEmail = (user.email || '').toLowerCase();
    const searchLower = search.toLowerCase();
    return (userName.includes(searchLower) || userEmail.includes(searchLower)) && isUserActive(user);
  });
  
  return (
    <div className="user-search-container">
      <div className="search-input-wrapper">
        <Search size={16} />
        <input
          type="text"
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          placeholder="Search employee by name or email..."
          className="user-search-input"
        />
      </div>
      
      {showDropdown && search && (
        <div className="user-dropdown">
          {filteredUsers.length === 0 ? (
            <div className="no-results">No users found</div>
          ) : (
            filteredUsers.slice(0, 10).map(user => {
              const displayName = getUserName(user);
              return (
                <div 
                  key={user.id} 
                  className={`user-option ${selectedUser?.id === user.id ? 'selected' : ''}`}
                  onClick={() => {
                    onSelect({...user, full_name: displayName});
                    setSearch(displayName);
                    setShowDropdown(false);
                  }}
                >
                  <div className="user-avatar-small">
                    {displayName.charAt(0).toUpperCase()}
                  </div>
                  <div className="user-info-small">
                    <span className="user-name">{displayName}</span>
                    <span className="user-email">{user.email}</span>
                  </div>
                  <span className="user-role-badge">{user.role || (user.roles && user.roles[0]) || 'User'}</span>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// Asset Checklist Component
const AssetChecklist = ({ assets, onReturn }) => {
  const pendingCount = assets.filter(a => a.status === 'pending_return').length;
  const assignedCount = assets.filter(a => a.status === 'assigned').length;
  
  return (
    <div className="asset-checklist">
      <div className="checklist-summary">
        <div className="summary-stat">
          <Package size={20} />
          <span className="stat-value">{assets.length}</span>
          <span className="stat-label">Total Assets</span>
        </div>
        <div className="summary-stat pending">
          <Clock size={20} />
          <span className="stat-value">{pendingCount}</span>
          <span className="stat-label">Pending Return</span>
        </div>
        <div className="summary-stat assigned">
          <AlertTriangle size={20} />
          <span className="stat-value">{assignedCount}</span>
          <span className="stat-label">Still Assigned</span>
        </div>
      </div>
      
      {assets.length === 0 ? (
        <div className="no-assets-message">
          <CheckCircle size={32} />
          <p>No assets assigned to this employee</p>
        </div>
      ) : (
        <div className="assets-list">
          {assets.map(asset => (
            <div key={asset.id} className={`checklist-asset status-${asset.status}`}>
              <div className="asset-main">
                <code className="asset-tag">{asset.asset_tag}</code>
                <span className="asset-name">{asset.name}</span>
                <span className={`status-indicator ${asset.status}`}>
                  {asset.status === 'pending_return' ? '⏳ Pending Return' : '📦 Assigned'}
                </span>
              </div>
              <div className="asset-meta">
                <span className="category">{asset.category}</span>
                {asset.serial_number && <span className="serial">S/N: {asset.serial_number}</span>}
              </div>
              {asset.status !== 'pending_return' && onReturn && (
                <button 
                  className="btn-small btn-return"
                  onClick={() => onReturn(asset)}
                  title="Process Return"
                >
                  Process Return
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Offboarding Modal Component
const OffboardingModal = ({ isOpen, onClose, allUsers, onAssetsUpdated, initialUser = null }) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(false);
  const [flagging, setFlagging] = useState(false);

  useEffect(() => {
    if (isOpen && initialUser) setSelectedUser(initialUser);
  }, [isOpen, initialUser]);
  
  const fetchChecklist = useCallback(async (userId) => {
    setLoading(true);
    try {
      const data = await apiCall(`/api/it-assets/offboarding/checklist/${userId}`);
      setChecklist(data);
    } catch (error) {
      toast.error('Failed to load asset checklist');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    if (selectedUser) {
      fetchChecklist(selectedUser.id);
    } else {
      setChecklist(null);
    }
  }, [selectedUser, fetchChecklist]);
  
  const handleFlagForOffboarding = async () => {
    if (!selectedUser) return;
    
    setFlagging(true);
    try {
      const result = await apiCall(`/api/it-assets/offboarding/${selectedUser.id}`, {
        method: 'POST'
      });
      
      toast.success(`${result.flagged_count} assets flagged for return`);
      fetchChecklist(selectedUser.id);
      if (onAssetsUpdated) onAssetsUpdated();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setFlagging(false);
    }
  };
  
  const handleReturnAsset = async (asset) => {
    try {
      await apiCall(`/api/it-assets/${asset.id}/return`, {
        method: 'POST',
        body: JSON.stringify({ condition_at_return: asset.condition || 'good' })
      });
      toast.success(`${asset.name} returned successfully`);
      fetchChecklist(selectedUser.id);
      if (onAssetsUpdated) onAssetsUpdated();
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  const handleClose = () => {
    setSelectedUser(null);
    setChecklist(null);
    onClose();
  };
  
  if (!isOpen) return null;
  
  const hasAssignedAssets = checklist?.assets?.some(a => a.status === 'assigned');
  
  return (
    <div className="modal-overlay avm-overlay" onClick={handleClose}>
      <div className="avm-modal obm-modal" onClick={e => e.stopPropagation()} data-testid="offboarding-modal">
        <div className="avm-header">
          <div className="avm-header-left">
            <div className="avm-header-icon"><UserMinus size={17} /></div>
            <div>
              <h3>Employee Offboarding — Asset Recovery</h3>
              <p>Select an employee to view and recover their assigned IT assets</p>
            </div>
          </div>
          <button type="button" className="avm-close" onClick={handleClose} data-testid="offboarding-close-x"><X size={16} /></button>
        </div>
        
        <div className="avm-body obm-body">
          <UserSearch 
            users={allUsers}
            selectedUser={selectedUser}
            onSelect={setSelectedUser}
          />
          
          {selectedUser && (
            <div className="selected-user-info">
              <div className="user-card">
                <div className="user-avatar-large">
                  {(selectedUser.full_name || selectedUser.email).charAt(0).toUpperCase()}
                </div>
                <div className="user-details">
                  <h4>{selectedUser.full_name || 'No Name'}</h4>
                  <span className="email">{selectedUser.email}</span>
                  <span className="role">{selectedUser.role}</span>
                </div>
              </div>
            </div>
          )}
          
          {loading && (
            <div className="loading-state">Loading asset checklist...</div>
          )}
          
          {checklist && !loading && (
            <AssetChecklist 
              assets={checklist.assets || []}
              onReturn={handleReturnAsset}
            />
          )}
        </div>

        {/* Sticky footer — always visible */}
        <div className="avm-footer">
          <button type="button" className="avm-btn-cancel" onClick={handleClose} data-testid="offboarding-close-btn">Close</button>
          {hasAssignedAssets && (
            <button 
              className="avm-btn-save obm-flag-btn"
              onClick={handleFlagForOffboarding}
              disabled={flagging}
              data-testid="offboarding-flag-btn"
            >
              <AlertTriangle size={15} />
              {flagging ? 'Flagging...' : 'Flag All Assets for Return'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OffboardingModal;
